import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { Form, PrimaryButton, FieldTextInput, FieldCheckbox, NamedLink } from '../../../components';

import css from './LoginForm.module.css';

const LoginFormComponent = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        intl,
        invalid,
        values,
        errors,
      } = fieldRenderProps;

      // email
      const emailLabel = intl.formatMessage({
        id: 'LoginForm.emailLabel',
      });
      const emailRequiredMessage = intl.formatMessage({
        id: 'LoginForm.emailRequired',
      });
      const emailRequired = validators.required(emailRequiredMessage);
      const emailInvalidMessage = intl.formatMessage({
        id: 'LoginForm.emailInvalid',
      });
      const emailValid = validators.emailFormatValid(emailInvalidMessage);

      // password
      const passwordLabel = intl.formatMessage({
        id: 'LoginForm.passwordLabel',
      });
      const passwordRequiredMessage = intl.formatMessage({
        id: 'LoginForm.passwordRequired',
      });
      const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = invalid || submitInProgress;

      const forgotPasswordText = intl.formatMessage({
        id: 'LoginForm.forgotPassword',
      });

      const rememberMeText = intl.formatMessage({
        id: 'LoginForm.rememberMe',
      });

      const signInHeaderText = intl.formatMessage({
        id: 'LoginForm.signIn',
      });

      const signUpPromptText = intl.formatMessage({
        id: 'LoginForm.signUpPrompt',
      });

      const signUpText = intl.formatMessage({
        id: 'LoginForm.signUp',
      });

      const signupRouteName = props.signupRouteName || 'SignupPage';

      const signInWithText = intl.formatMessage({
        id: 'LoginForm.signInWith',
      });

      const signInWithGoogleText = intl.formatMessage({
        id: 'LoginForm.signInWithGoogle',
      });

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <div className={css.header}>
            <h1 className={css.title}>{signInHeaderText}</h1>
          </div>

          <div className={css.formFields}>
            <FieldTextInput
              type="email"
              id={formId ? `${formId}.email` : 'email'}
              name="email"
              autoComplete="email"
              label={emailLabel}
              validate={validators.composeValidators(emailRequired, emailValid)}
            />
            <FieldTextInput
              type="password"
              id={formId ? `${formId}.password` : 'password'}
              name="password"
              autoComplete="current-password"
              label={passwordLabel}
              validate={passwordRequired}
            />
          </div>

          <div className={css.rememberWrapper}>
            <FieldCheckbox
              id={formId ? `${formId}.rememberMe` : 'rememberMe'}
              name="rememberMe"
              label={rememberMeText}
              value="remember"
              textClassName={css.rememberMe}
            />
            <NamedLink
              name="PasswordRecoveryPage"
              className={css.forgotPasswordLink}
              to={{
                search:
                  values?.email && !errors?.email
                    ? `email=${encodeURIComponent(values.email)}`
                    : '',
              }}
            >
              {forgotPasswordText}
            </NamedLink>
          </div>

          <PrimaryButton
            type="submit"
            inProgress={submitInProgress}
            disabled={submitDisabled}
            className={css.signInButton}
          >
            <FormattedMessage id="LoginForm.logIn" />
          </PrimaryButton>

          <div className={css.signUpPrompt}>
            <span className={css.signUpPromptText}>{signUpPromptText}</span>{' '}
            <NamedLink name={signupRouteName} className={css.signUpLink}>
              <span className={css.signUpNoWrap}>{signUpText}</span>
            </NamedLink>
          </div>
        </Form>
      );
    }}
  />
);

/**
 * A component that renders the login form.
 *
 * @component
 * @param {Object} props
 * @param {string} props.rootClassName - The root class name that overrides the default class css.root
 * @param {string} props.className - The class that extends the root class
 * @param {string} props.formId - The form id
 * @param {boolean} props.inProgress - Whether the form is in progress
 * @returns {JSX.Element}
 */
const LoginForm = props => {
  const intl = useIntl();
  return <LoginFormComponent {...props} intl={intl} />;
};

export default LoginForm;
